import { useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Table,
  Separator,
} from '@chakra-ui/react'
import {
  X,
  ChevronUp,
  ChevronDown,
  FileText,
  Calendar,
  CreditCard,
  Clock,
  AlertTriangle,
  Truck,
  Check,
} from 'lucide-react'
import { useSupplierInvoice } from '../hooks'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { SupplierInvoiceStatus, PaymentInvoiceStatus } from '../types'
import {
  SUPPLIER_INVOICE_STATUS_LABELS,
  SUPPLIER_INVOICE_STATUS_COLORS,
  PAYMENT_INVOICE_STATUS_LABELS,
  PAYMENT_INVOICE_STATUS_COLORS,
} from '../types'

interface SupplierInvoiceDetailDrawerProps {
  invoiceId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function SupplierInvoiceDetailDrawer({
  invoiceId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: SupplierInvoiceDetailDrawerProps) {
  const { data: invoice, isLoading, error } = useSupplierInvoice(invoiceId ?? undefined)

  const handleInteractOutside = useCallback(
    (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
      const target = e.detail.originalEvent.target as HTMLElement
      const tableRow = target.closest('[data-invoice-row]') as HTMLElement
      if (tableRow) return
      onClose()
    },
    [onClose]
  )

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatCurrency = (value: number | null | undefined) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isOverdue = (dueDate: string | null | undefined, status: string, paymentStatus: string) => {
    if (!dueDate || status !== 'VALIDATED' || paymentStatus === 'PAID') return false
    return new Date(dueDate) < new Date()
  }

  const getDaysOverdue = (dueDate: string | null | undefined) => {
    if (!dueDate) return 0
    const due = new Date(dueDate)
    const today = new Date()
    return Math.ceil((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24))
  }

  const getAccountCode = (line: any) => {
    if (line.residence_chart_accounts?.custom_code) return line.residence_chart_accounts.custom_code
    if (line.residence_chart_accounts?.chart_accounts?.code) return line.residence_chart_accounts.chart_accounts.code
    return '???'
  }

  const getAccountName = (line: any) => {
    if (line.residence_chart_accounts?.custom_name) return line.residence_chart_accounts.custom_name
    if (line.residence_chart_accounts?.chart_accounts?.name) return line.residence_chart_accounts.chart_accounts.name
    return '-'
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="800px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {invoice && (
                  <>
                    <HStack gap={2} mb={1} flexWrap="wrap">
                      <Badge variant="outline" size="sm" fontFamily="mono">
                        {invoice.internal_reference}
                      </Badge>
                      <Badge
                        colorPalette={
                          SUPPLIER_INVOICE_STATUS_COLORS[invoice.status as SupplierInvoiceStatus]
                        }
                        size="sm"
                      >
                        {SUPPLIER_INVOICE_STATUS_LABELS[invoice.status as SupplierInvoiceStatus]}
                      </Badge>
                      {invoice.status !== 'CANCELLED' && (
                        <Badge
                          colorPalette={
                            PAYMENT_INVOICE_STATUS_COLORS[
                              invoice.payment_status as PaymentInvoiceStatus
                            ]
                          }
                          size="sm"
                        >
                          {
                            PAYMENT_INVOICE_STATUS_LABELS[
                              invoice.payment_status as PaymentInvoiceStatus
                            ]
                          }
                        </Badge>
                      )}
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      Facture {invoice.invoice_number}
                    </Heading>
                    {invoice.suppliers && (
                      <Text fontSize="sm" color="gray.500">
                        {invoice.suppliers.name}
                      </Text>
                    )}
                  </>
                )}
                {!invoice && <Heading size="md">Détail de la facture</Heading>}
              </Box>

              {/* Close */}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                <X size={18} />
              </IconButton>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Facture non trouvée" />}

            {invoice && (
              <VStack align="stretch" gap={0} flex={1} height="100%" overflow="auto">
                {/* Informations principales */}
                <HStack
                  gap={4}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="blue.500">
                      <Calendar size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Date facture
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {formatDate(invoice.invoice_date)}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box
                      color={
                        isOverdue(invoice.due_date, invoice.status, invoice.payment_status)
                          ? 'red.500'
                          : 'orange.500'
                      }
                    >
                      <Calendar size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Échéance
                      </Text>
                      <HStack gap={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={
                            isOverdue(invoice.due_date, invoice.status, invoice.payment_status)
                              ? 'red.600'
                              : 'gray.800'
                          }
                        >
                          {formatDate(invoice.due_date)}
                        </Text>
                        {isOverdue(invoice.due_date, invoice.status, invoice.payment_status) && (
                          <AlertTriangle size={14} color="var(--chakra-colors-red-500)" />
                        )}
                      </HStack>
                      {isOverdue(invoice.due_date, invoice.status, invoice.payment_status) && (
                        <Text fontSize="xs" color="red.500">
                          +{getDaysOverdue(invoice.due_date)} jours
                        </Text>
                      )}
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="green.500">
                      <CreditCard size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Total TTC
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color="green.600">
                        {formatCurrency(invoice.total_ttc)}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color={invoice.amount_remaining > 0 ? 'orange.500' : 'green.500'}>
                      {invoice.amount_remaining > 0 ? (
                        <AlertTriangle size={16} />
                      ) : (
                        <Check size={16} />
                      )}
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Reste dû
                      </Text>
                      <Text
                        fontSize="sm"
                        fontWeight="bold"
                        color={invoice.amount_remaining > 0 ? 'orange.600' : 'green.600'}
                      >
                        {formatCurrency(invoice.amount_remaining)}
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>

                {/* Fournisseur */}
                {invoice.suppliers && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <Truck size={16} />
                          <Heading size="sm">Fournisseur</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <HStack justify="space-between">
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {invoice.suppliers.name}
                            </Text>
                            {invoice.contracts && (
                              <Text fontSize="xs" color="gray.500">
                                Contrat: {invoice.contracts.label}
                              </Text>
                            )}
                          </VStack>
                          <Badge variant="outline" size="sm" fontFamily="mono">
                            {invoice.suppliers.code}
                          </Badge>
                        </HStack>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Lignes de facture */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between">
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Lignes de facture</Heading>
                        </HStack>
                        <Badge size="sm">{invoice.supplier_invoice_lines?.length || 0} ligne(s)</Badge>
                      </HStack>
                    </Card.Header>
                    <Card.Body p={0}>
                      <Table.Root size="sm">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader w="80px">Compte</Table.ColumnHeader>
                            <Table.ColumnHeader>Libellé</Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="80px">
                              Qté
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="100px">
                              PU HT
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="100px">
                              Montant HT
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="60px">
                              TVA
                            </Table.ColumnHeader>
                            <Table.ColumnHeader textAlign="right" w="100px">
                              TTC
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {invoice.supplier_invoice_lines?.map((line) => (
                            <Table.Row key={line.id}>
                              <Table.Cell>
                                <Text fontFamily="mono" fontSize="xs" color="blue.600">
                                  {getAccountCode(line)}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Text fontSize="sm">{line.description}</Text>
                                <Text fontSize="xs" color="gray.500">
                                  {getAccountName(line)}
                                </Text>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Text fontSize="sm">{line.quantity}</Text>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Text fontSize="sm" fontFamily="mono">
                                  {formatCurrency(line.unit_price_ht)}
                                </Text>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Text fontSize="sm" fontFamily="mono">
                                  {formatCurrency(line.amount_ht)}
                                </Text>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Text fontSize="xs" color="gray.500">
                                  {line.vat_rate}%
                                </Text>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Text fontSize="sm" fontFamily="mono" fontWeight="medium">
                                  {formatCurrency(line.amount_ttc)}
                                </Text>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                          {/* Totaux */}
                          <Table.Row bg="gray.100">
                            <Table.Cell colSpan={4} textAlign="right">
                              <Text fontWeight="medium">Total HT</Text>
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              <Text fontFamily="mono" fontWeight="medium">
                                {formatCurrency(invoice.total_ht)}
                              </Text>
                            </Table.Cell>
                            <Table.Cell />
                            <Table.Cell />
                          </Table.Row>
                          <Table.Row bg="gray.100">
                            <Table.Cell colSpan={4} textAlign="right">
                              <Text fontWeight="medium">TVA</Text>
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              <Text fontFamily="mono" fontWeight="medium">
                                {formatCurrency(invoice.total_vat)}
                              </Text>
                            </Table.Cell>
                            <Table.Cell />
                            <Table.Cell />
                          </Table.Row>
                          <Table.Row bg="gray.200">
                            <Table.Cell colSpan={4} textAlign="right">
                              <Text fontWeight="bold">Total TTC</Text>
                            </Table.Cell>
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell textAlign="right">
                              <Text fontFamily="mono" fontWeight="bold" color="green.600">
                                {formatCurrency(invoice.total_ttc)}
                              </Text>
                            </Table.Cell>
                          </Table.Row>
                        </Table.Body>
                      </Table.Root>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Paiements */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between">
                        <HStack>
                          <CreditCard size={16} />
                          <Heading size="sm">Paiements</Heading>
                        </HStack>
                      </HStack>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Montant payé
                          </Text>
                          <Text fontSize="sm" fontWeight="medium" fontFamily="mono" color="green.600">
                            {formatCurrency(invoice.amount_paid)}
                          </Text>
                        </HStack>
                        <HStack justify="space-between">
                          <Text fontSize="sm" color="gray.600">
                            Reste à payer
                          </Text>
                          <Text
                            fontSize="sm"
                            fontWeight="bold"
                            fontFamily="mono"
                            color={invoice.amount_remaining > 0 ? 'orange.600' : 'green.600'}
                          >
                            {formatCurrency(invoice.amount_remaining)}
                          </Text>
                        </HStack>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Description */}
                {invoice.description && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Description</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {invoice.description}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Notes */}
                {invoice.notes && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Notes</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <Text fontSize="sm" whiteSpace="pre-wrap">
                          {invoice.notes}
                        </Text>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Historique */}
                <Box px={6} pt={4} pb={6}>
                  <Card.Root variant="outline">
                    <Card.Header py={3}>
                      <Heading size="sm">Historique</Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        {invoice.receipt_date && (
                          <HStack gap={3}>
                            <Box color="blue.500">
                              <FileText size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                Reçue le
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(invoice.receipt_date)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}

                        {invoice.accounting_date && (
                          <HStack gap={3}>
                            <Box color="purple.500">
                              <Calendar size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                Date comptable
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDate(invoice.accounting_date)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}

                        <Separator />

                        <HStack gap={3}>
                          <Box color="gray.400">
                            <Clock size={16} />
                          </Box>
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              Créée le
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatDateTime(invoice.created_at)}
                            </Text>
                          </VStack>
                        </HStack>

                        {invoice.updated_at && invoice.updated_at !== invoice.created_at && (
                          <HStack gap={3}>
                            <Box color="blue.500">
                              <Clock size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                Modifiée le
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(invoice.updated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
